import { resolvePhysicalTrayQuantity } from "./physicalTrayQuantity";
import { useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { ordersApi, type Order } from "../../services/ordersApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CheckCircle2, CircleDot, CircleDashed } from "lucide-react";
import {
  MoreHorizontal,
  Search,
  Eye,
  FileText,
  BadgeCheck,
  Sprout,
  Wallet,
  ReceiptText,
  XCircle,
  ChevronDown,
  ChevronRight,
  PackageCheck,
  FilePenLine,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ViewOrderDialog } from "./ViewOrderDialog";
import { UpdateOrderStatusDialog } from "./UpdateOrderStatusDialog";
import { CancelOrderDialog } from "./CancelOrderDialog";
import { RecordPaymentDialog } from "./RecordPaymentDialog";
import { ViewSowingInstructionsModal } from "../sowingInstructions/ViewSowingInstructionsModal";
import { GenerateInvoiceFromOrderDialog } from "./GenerateInvoiceFromOrderDialog";
import { ViewOrderPaymentsDialog } from "./ViewOrderPaymentsDialog";
import { OverrideConfirmDialog } from "./OverrideConfirmDialog";
import { ReviewPaymentOverrideDialog } from "./ReviewPaymentOverrideDialog";
import { AssignStockDialog } from "./AssignStockDialog";
import { CreateOrderAmendmentDialog } from "./CreateOrderAmendmentDialog";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { PaginationBar } from "@/components/ui/PaginationBar";

interface OrdersTableProps {
  orders: Order[];
  search: string;
  onSearchChange: (value: string) => void;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: 2,
  }).format(value);

const getBalanceAmount = (order: Order) => {
  const total = Number(order.totalAmount ?? 0);
  const paid = Number(order.paidAmount ?? 0);
  const computed = total - paid;
  const balance = Number(order.balanceAmount ?? computed);
  return Math.max(balance, 0);
};

const getExpectedAmount = (order: Order) => Number(order.totalAmount ?? 0);

const getPaidAmount = (order: Order) => Number(order.paidAmount ?? 0);

const formatDateOrDash = (value?: string) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime()) || d.getFullYear() < 1971) return "-";
  return d.toLocaleDateString();
};

const formatExpandedQty = (quantity: number, unit: number, capacity?: number | null, legacyPieces = false) => {
  if (unit !== 11 || quantity === Math.floor(quantity)) {
    return `${quantity}`;
  }

  const sellablePerTray = capacity;
  if (!sellablePerTray) {
    return `${quantity}`;
  }

  const pieces = legacyPieces ? String(Math.round(quantity * sellablePerTray)) : resolvePhysicalTrayQuantity(String(quantity), sellablePerTray).pieces;
  if (pieces == null) return `${quantity}`;
  return `${quantity} (${pieces} pcs)`;
};

const ExpoOrderBadge = ({ name }: { name?: string | null }) =>
  name ? (
    <Badge
      variant="outline"
      className="w-fit border-cyan-300 bg-cyan-50 text-[10px] font-medium text-cyan-800"
    >
      Expo: {name}
    </Badge>
  ) : null;

/** Compact fulfilment indicator for the orders list (#1). Renders an icon + tooltip with the
 *  per-line ordered/supplied/pending breakdown so partial fulfilment is visible at a glance. */
const FulfilmentIndicator = ({ order }: { order: Order }) => {
  const totalOrdered = order.orderItems.reduce((s, i) => s + (i.quantity ?? 0), 0);
  const totalSupplied = order.orderItems.reduce((s, i) => s + (i.quantityFulfilled ?? 0), 0);
  const totalPending = Math.max(0, totalOrdered - totalSupplied);
  const summary = order.fulfillmentSummary
    ?? (totalSupplied >= totalOrdered && totalOrdered > 0
      ? "FullyFulfilled"
      : totalSupplied > 0
        ? "PartiallyFulfilled"
        : "AwaitingFulfilment");

  const { icon, label, className } = (() => {
    switch (summary) {
      case "FullyFulfilled":
        return { icon: <CheckCircle2 className="h-4 w-4" />, label: "Fully fulfilled", className: "text-emerald-600" };
      case "PartiallyFulfilled":
        return { icon: <CircleDot className="h-4 w-4" />, label: "Partially fulfilled", className: "text-amber-600" };
      default:
        return { icon: <CircleDashed className="h-4 w-4" />, label: "Awaiting fulfilment", className: "text-muted-foreground" };
    }
  })();

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center ${className}`} aria-label={label}>
            {icon}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          <p className="font-semibold mb-1">{label}</p>
          <p>{`${totalSupplied} of ${totalOrdered} supplied · ${totalPending} pending`}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const STATUS_COLORS: Record<string, string> = {
  Placed: "outline",
  Confirmed: "outline",
  Processing: "secondary",
  SowingInitiated: "secondary",
  Growing: "default",
  ReadyForDelivery: "default",
  ReadyForDispatch: "default",
  PartiallyDispatched: "secondary",
  Dispatched: "default",
  Delivered: "secondary",
  Cancelled: "destructive",
  WalkInSale: "default",
};

const canShowAssignStockAction = (order: Order): boolean => {
  const hasUnfulfilledCompanyItem = order.orderItems.some(
    (item) => item.isCompanySeedling && !item.isFullyFulfilled
  );

  return (
    order.status === "Confirmed" ||
    order.status === "SowingInitiated" ||
    order.status === "Growing" ||
    ((order.status === "PartiallyDispatched" || order.status === "Dispatched" || order.status === "Delivered") && hasUnfulfilledCompanyItem)
  );
};

export const OrdersTable = ({ orders, search, onSearchChange, currentPage, pageSize, totalCount, totalPages, onPageChange, onPageSizeChange }: OrdersTableProps) => {
  const { hasPermission } = useAuthStore();
  const canUpdateStatus = hasPermission("orders.update_status");
  const canConfirmPayment = hasPermission("orders.confirm_payment");
  const canCancel = hasPermission("orders.cancel");
  const canRequestPaymentOverride = hasPermission("orders.request_payment_threshold_override");
  const canApprovePaymentOverride = hasPermission("orders.approve_payment_threshold_override");
  const canRevokePaymentOverride = hasPermission("orders.revoke_payment_threshold_override");
  const canViewPaymentOverride = hasPermission("orders.view_payment_threshold_override");
  const canCreateInvoice = hasPermission("invoices.create");
  const canAmend = hasPermission("orders.amend_request");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelInitialReason, setCancelInitialReason] = useState<string | undefined>();
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [viewPaymentsOpen, setViewPaymentsOpen] = useState(false);
  const [sowingInstructionsOpen, setSowingInstructionsOpen] = useState(false);
  const [generateInvoiceOpen, setGenerateInvoiceOpen] = useState(false);
  const [overrideConfirmOpen, setOverrideConfirmOpen] = useState(false);
  const [reviewOverrideOpen, setReviewOverrideOpen] = useState(false);
  const [assignStockOpen, setAssignStockOpen] = useState(false);
  const [amendDialogOpen, setAmendDialogOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const shouldFetchOverrideState = canViewPaymentOverride;
  const overrideQueries = useQueries({
    queries: orders.map((order) => ({
      queryKey: ["orders", order.id, "payment-threshold-overrides"],
      queryFn: () => ordersApi.getPaymentThresholdOverrides(order.id),
      enabled: shouldFetchOverrideState && order.status === "Placed",
      staleTime: 30_000,
    })),
  });

  const workAuthorizationQueries = useQueries({
    queries: orders.map((order) => ({
      queryKey: ["orders", order.id, "work-authorization-status"],
      queryFn: () => ordersApi.getWorkAuthorizationStatus(order.id),
      enabled: shouldFetchOverrideState && order.status === "Placed",
      staleTime: 30_000,
    })),
  });

  const getOverrideAction = (order: Order, index: number): { label: string; tone?: "warning" } | null => {
    if (order.status !== "Placed") return null;
    if (!canViewPaymentOverride && !canRequestPaymentOverride && !canApprovePaymentOverride && !canRevokePaymentOverride) return null;

    const history = overrideQueries[index]?.data ?? [];
    const pending = history.find((item) => item.status === "Pending");
    const approved = history.find((item) => item.status === "Approved");
    const hasHistory = history.length > 0;
    const workStatus = workAuthorizationQueries[index]?.data?.status;

    if (pending) {
      if (canApprovePaymentOverride) return { label: "Review Override Request" };
      return { label: "View Override Status" };
    }

    if (approved) {
      if (canRevokePaymentOverride || canApprovePaymentOverride || canViewPaymentOverride) {
        return { label: "View Approved Override" };
      }
    }

    if (hasHistory) {
      return { label: "View Override Status" };
    }

    if (canRequestPaymentOverride && workStatus && workStatus !== "PaymentThresholdSatisfied") {
      return { label: "Request Payment Threshold Override", tone: "warning" };
    }

    return null;
  };

  const toggleExpand = (id: string) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setViewOpen(true);
  };

  const handleStatusUpdate = (order: Order) => {
    setSelectedOrder(order);
    setStatusDialogOpen(true);
  };

  const handleCancelOrder = (order: Order) => {
    setSelectedOrder(order);
    setCancelDialogOpen(true);
  };

  const handleRecordPayment = (order: Order) => {
    setSelectedOrder(order);
    setPaymentDialogOpen(true);
  };

  const handleViewPayments = (order: Order) => {
    setSelectedOrder(order);
    setViewPaymentsOpen(true);
  };

  const handleGenerateInvoice = (order: Order) => {
    setSelectedOrder(order);
    setGenerateInvoiceOpen(true);
  };

  return (
    <>
      <div className="space-y-4">
        <div className="relative w-72 ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 pointer-events-none" />
          <Input
            placeholder="Search orders…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 pr-4 bg-background focus-visible:ring-primary/40"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Expected Delivery</TableHead>
                <TableHead>Planting Date</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Billing</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Fulfilment</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order, rowIndex) => {
                const balanceAmount = getBalanceAmount(order);
                const expectedAmount = getExpectedAmount(order);
                const paidAmount = getPaidAmount(order);
                const isExpanded = expandedRows.has(order.id);

                return (
                  <>
                  <TableRow key={order.id}>
                    <TableCell className="font-mono text-sm">
                      <div className="flex flex-col gap-1">
                        <span>{order.orderNumber}</span>
                        <ExpoOrderBadge name={order.expoName ?? (order.expoId ? "Expo sale" : null)} />
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {order.customerName}
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => toggleExpand(order.id)}
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {isExpanded
                          ? <ChevronDown className="w-3 h-3" />
                          : <ChevronRight className="w-3 h-3" />}
                        {order.orderItems.length} item{order.orderItems.length !== 1 ? "s" : ""}
                      </button>
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDateOrDash(order.expectedDeliveryDate)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDateOrDash(order.plannedPlantingDate)}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      <div className="space-y-0.5 whitespace-nowrap">
                        <div className="flex justify-end gap-2">
                          <span className="text-muted-foreground">Expected</span>
                          <span className="font-medium text-foreground">{formatCurrency(expectedAmount)}</span>
                        </div>
                        <div className="flex justify-end gap-2 text-xs">
                          <span className="text-muted-foreground">Paid</span>
                          <span className="font-medium">{formatCurrency(paidAmount)}</span>
                        </div>
                        <div className="flex justify-end gap-2 text-xs">
                          <span className="text-muted-foreground">Balance</span>
                          <span
                            className={
                              balanceAmount > 0
                                ? "font-semibold text-amber-700"
                                : "font-semibold text-emerald-700"
                            }
                          >
                            {formatCurrency(balanceAmount)}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Badge variant={balanceAmount > 0 ? "outline" : "secondary"}>
                          {order.paymentStatus || (balanceAmount > 0 ? "Pending" : "Paid")}
                        </Badge>
                        <p className="text-xs text-muted-foreground">
                          Work Authorization: {workAuthorizationQueries[rowIndex]?.data?.status ?? "-"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Due: {formatDateOrDash(order.paymentDueDate)}
                        </p>
                        <p className="text-xs text-muted-foreground max-w-[180px] truncate">
                          Invoice: {order.invoiceReference || "-"}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          STATUS_COLORS[order.status] as
                          | "outline"
                          | "secondary"
                          | "default"
                          | "destructive"
                        }
                      >
                        {order.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <FulfilmentIndicator order={order} />
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDateOrDash(order.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => handleViewOrder(order)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {canUpdateStatus && (
                            <DropdownMenuItem
                              onClick={() => handleStatusUpdate(order)}
                              disabled={
                                order.status === "Cancelled" ||
                                order.status === "Delivered"
                              }
                            >
                              <BadgeCheck className="w-4 h-4 mr-2" />
                              Update Status
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedOrder(order);
                              setSowingInstructionsOpen(true);
                            }}
                          >
                            <Sprout className="w-4 h-4 mr-2" />
                            View Sowing Instructions
                          </DropdownMenuItem>
                          {canConfirmPayment && (
                            <DropdownMenuItem
                              onClick={() => handleRecordPayment(order)}
                              disabled={balanceAmount <= 0 || order.status === "Cancelled"}
                            >
                              <Wallet className="w-4 h-4 mr-2" />
                              {balanceAmount > 0 ? "Record Payment" : "Fully Paid"}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleViewPayments(order)}
                          >
                            <ReceiptText className="w-4 h-4 mr-2" />
                            View Payments
                          </DropdownMenuItem>
                          {canCreateInvoice && (
                            <DropdownMenuItem
                              onClick={() => handleGenerateInvoice(order)}
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Generate AR Invoice
                            </DropdownMenuItem>
                          )}
                          {canCancel && (
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleCancelOrder(order)}
                              disabled={order.status === "Cancelled"}
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Cancel Order
                            </DropdownMenuItem>
                          )}
                          {canAmend && (
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedOrder(order);
                                setAmendDialogOpen(true);
                              }}
                              disabled={
                                order.status === "Cancelled" ||
                                order.status === "PartiallyDispatched" ||
                                order.status === "Dispatched" ||
                                order.status === "Delivered"
                              }
                            >
                              <FilePenLine className="w-4 h-4 mr-2" />
                              Amend Order
                            </DropdownMenuItem>
                          )}
                          {/* Assign Stock — also allow partially fulfilled delivered/dispatched orders */}
                          {canShowAssignStockAction(order) && (
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedOrder(order);
                                setAssignStockOpen(true);
                              }}
                            >
                              <PackageCheck className="w-4 h-4 mr-2" />
                              Assign Stock
                            </DropdownMenuItem>
                          )}
                          {(() => {
                            const action = getOverrideAction(order, rowIndex);
                            if (!action) return null;
                            return (
                              <DropdownMenuItem
                                className={action.tone === "warning" ? "text-orange-600" : undefined}
                                onClick={() => {
                                  setSelectedOrder(order);
                                  if (action.label === "Request Payment Threshold Override") {
                                    setOverrideConfirmOpen(true);
                                  } else {
                                    setReviewOverrideOpen(true);
                                  }
                                }}
                              >
                                <BadgeCheck className="w-4 h-4 mr-2" />
                                {action.label}
                              </DropdownMenuItem>
                            );
                          })()}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow key={`${order.id}-items`} className="bg-muted/40 hover:bg-muted/40">
                      <TableCell colSpan={11} className="py-2 px-6">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-muted-foreground border-b">
                              <th className="text-left py-1 pr-4 font-medium">Product</th>
                              <th className="text-left py-1 pr-4 font-medium">Variety</th>
                              <th className="text-right py-1 pr-4 font-medium">Qty</th>
                              <th className="text-left py-1 pr-4 font-medium">Tray</th>
                              <th className="text-right py-1 font-medium">Unit Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.orderItems.map((item, idx) => (
                              <tr key={idx} className="border-b last:border-0">
                                <td className="py-1 pr-4">{item.productName}</td>
                                <td className="py-1 pr-4 text-muted-foreground">{item.variety || "—"}</td>
                                <td className="py-1 pr-4 text-right font-medium">{formatExpandedQty(item.quantity, item.unit, item.pricingMode === 2 ? item.sellablePerTray : item.trayCapacity, item.pricingMode === 2)}</td>
                                <td className="py-1 pr-4 text-muted-foreground">{item.trayTypeName || "—"}</td>
                                <td className="py-1 text-right">{item.unitPrice != null ? formatCurrency(item.unitPrice) : "—"}</td>
                              </tr>
                            ))}
                            {order.transportCost != null && order.transportCost > 0 && (
                              <tr className="border-t border-dashed">
                                <td className="py-1 pr-4 text-muted-foreground italic">
                                  Transport{order.transportDestinationName ? ` — ${order.transportDestinationName}` : ""}
                                </td>
                                <td className="py-1 pr-4" />
                                <td className="py-1 pr-4 text-right">1</td>
                                <td className="py-1 pr-4" />
                                <td className="py-1 text-right font-medium">{formatCurrency(order.transportCost)}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </TableCell>
                    </TableRow>
                  )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {orders.length === 0 && (
          <div className="text-center py-8 text-gray-500">No orders found</div>
        )}

        <PaginationBar
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </div>

      {selectedOrder && (
        <>
          <ViewOrderDialog
            order={selectedOrder}
            open={viewOpen}
            onOpenChange={setViewOpen}
          />
          <UpdateOrderStatusDialog
            order={selectedOrder}
            open={statusDialogOpen}
            onOpenChange={setStatusDialogOpen}
          />
          <ViewSowingInstructionsModal
            order={selectedOrder}
            open={sowingInstructionsOpen}
            onOpenChange={setSowingInstructionsOpen}
          />
          <CancelOrderDialog
            order={selectedOrder}
            open={cancelDialogOpen}
            onOpenChange={(open) => {
              setCancelDialogOpen(open);
              if (!open) setCancelInitialReason(undefined);
            }}
            initialReason={cancelInitialReason}
          />
          <RecordPaymentDialog
            order={selectedOrder}
            open={paymentDialogOpen}
            onOpenChange={setPaymentDialogOpen}
          />
          <ViewOrderPaymentsDialog
            order={selectedOrder}
            open={viewPaymentsOpen}
            onOpenChange={setViewPaymentsOpen}
          />
          <GenerateInvoiceFromOrderDialog
            order={selectedOrder}
            open={generateInvoiceOpen}
            onOpenChange={setGenerateInvoiceOpen}
          />
          <OverrideConfirmDialog
            order={selectedOrder}
            open={overrideConfirmOpen}
            onOpenChange={setOverrideConfirmOpen}
          />
          <ReviewPaymentOverrideDialog order={selectedOrder} open={reviewOverrideOpen} onOpenChange={setReviewOverrideOpen} />
          <AssignStockDialog
            order={selectedOrder}
            open={assignStockOpen}
            onOpenChange={setAssignStockOpen}
          />
          <CreateOrderAmendmentDialog
            order={selectedOrder}
            open={amendDialogOpen}
            onOpenChange={setAmendDialogOpen}
            onRequiresCancellation={(reason) => {
              setAmendDialogOpen(false);
              setCancelInitialReason(reason);
              setCancelDialogOpen(true);
            }}
          />
        </>
      )}
    </>
  );
};
